//	HTML templates for the game list and the board. Pure functions of state;
//	main.js owns the state and the DOM. Every user-supplied string goes
//	through esc().
import {
	PLACE,
	FILTER_KEYS,
	FILTER_LABEL,
	fmt,
	ago,
	place,
	avatarUrl,
	initial,
	filterSummary,
	filterOptions,
	pageRange,
} from './format.js';

export function esc(s) {
	return String(s ?? '').replace(
		/[&<>"']/g,
		(c) => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'})[c],
	);
}

const PERSON =
	'<svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="8" r="4.2"/><path d="M3.5 21c0-4.7 3.8-7.5 8.5-7.5s8.5 2.8 8.5 7.5z"/></svg>';
//	Avatar tint from the username, stable per player
const TINTS = ['#fdda16', '#f35588', '#42b883', '#3df2ff', '#ef8a1d', '#bdbdbd', '#b9a8ff'];
function tint(name) {
	let h = 0;
	for (const ch of String(name || '')) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
	return TINTS[h % TINTS.length];
}

//	Avatar URLs are presigned and the server signs them afresh on every
//	response, but each signature is valid for days. Key avatars by file path,
//	keep serving the URL we already showed (a browser-cache hit, no request),
//	and only pick up a new signature — loaded in the background first, so it
//	never blinks — once the one we hold is close to expiring.
const AVATARS = new Map();
const RENEW_BEFORE_MS = 6 * 60 * 60 * 1000;

//	Expiry of a SigV4 presigned URL: X-Amz-Date (YYYYMMDDTHHMMSSZ) + X-Amz-Expires
//	seconds. Unknown shape → Infinity (never renew on our own initiative).
export function signedUrlExpiry(url) {
	try {
		const q = new URL(url).searchParams;
		const date = q.get('X-Amz-Date');
		const secs = Number(q.get('X-Amz-Expires'));
		const m = date && date.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/);
		if (!m || !secs) return Infinity;
		return Date.UTC(+m[1], m[2] - 1, +m[3], +m[4], +m[5], +m[6]) + secs * 1000;
	} catch (err) {
		return Infinity;
	}
}

export function avatarSrc(
	url,
	load = (src, onDone) => {
		const img = new Image();
		img.onload = () => onDone(true);
		img.onerror = () => onDone(false);
		img.src = src;
	},
	now = Date.now,
) {
	if (!url) return null;
	const key = url.split('?')[0];
	const hit = AVATARS.get(key);
	if (!hit) {
		AVATARS.set(key, {src: url, pending: null});
		return url;
	}
	const stillGood = signedUrlExpiry(hit.src) - now() > RENEW_BEFORE_MS;
	if (hit.src !== url && hit.pending !== url && !stillGood) {
		hit.pending = url;
		load(url, (ok) => {
			if (ok) hit.src = url;
			hit.pending = null;
		});
	}
	return hit.src;
}

export function avatar(player) {
	const url = avatarSrc(avatarUrl(player));
	if (url) return `<span class="av"><img src="${esc(url)}" alt="" decoding="sync"></span>`;
	if (!player?.username) return `<span class="av none">${PERSON}</span>`;
	return `<span class="av" style="background:${tint(player.username)}">${esc(initial(player.username))}</span>`;
}

export function games({games, currentID}) {
	return (
		'<h3>Games</h3>' +
		games
			.map(
				(g) =>
					`<button class="g" data-game="${g.gameVersionID}" aria-pressed="${g.gameVersionID === currentID}">` +
					`<b class="clip">${esc(g.title)}</b><small>${g.players ? `${fmt(g.players)} players` : 'none yet'}</small></button>`,
			)
			.join('')
	);
}

function filters({filters: f, locations}) {
	const selects = FILTER_KEYS.map((k, i) => {
		const disabled = i > 0 && !f[FILTER_KEYS[i - 1]];
		const opts = filterOptions(locations, f, k);
		return (
			`<select class="sel${f[k] ? ' on' : ''}" data-filter="${k}" aria-label="${k}"${disabled ? ' disabled' : ''}>` +
			`<option value="">${FILTER_LABEL[k]}</option>` +
			opts.map((o) => `<option${f[k] === o ? ' selected' : ''}>${esc(o)}</option>`).join('') +
			'</select>'
		);
	}).join('');
	const clear = f.country
		? '<button class="clear" type="button" data-filter-clear>Clear</button>'
		: '';
	return `<div class="filters">${selects}${clear}</div>`;
}

function chips(rows) {
	return rows
		.slice(0, 3)
		.map(
			(r, i) =>
				`<div class="chip p${i + 1}"><span class="place">${PLACE[i]}</span>${avatar(r.player)}` +
				`<b class="clip">${esc(r.player?.username || 'Anonymous')}</b><span class="pts num">${fmt(r.score)}</span></div>`,
		)
		.join('');
}

function podium(rows, now) {
	return rows
		.slice(0, 3)
		.map(
			(r, i) =>
				`<div class="pod p${i + 1}">${avatar(r.player)}<div class="plc">${PLACE[i]}</div>` +
				`<div class="who clip">${esc(r.player?.username || 'Anonymous')}</div><div class="pts num">${fmt(r.score)}</div>` +
				`<div class="meta"><span class="clip">${esc(place(r.location))}</span><span class="when">${ago(r.achievedAt, now)}</span></div></div>`,
		)
		.join('');
}

function row(r, now) {
	return (
		`<div class="row"><div class="rk num">${r.rank}</div>` +
		`<div class="player">${avatar(r.player)}<span class="clip">${esc(r.player?.username || 'Anonymous')}</span></div>` +
		`<div class="sc num">${fmt(r.score)}</div>` +
		`<div class="city clip">${esc(r.location?.city || '')}</div>` +
		`<div class="st hide-s">${esc(r.location?.state || '')}</div>` +
		`<div class="cc hide-s">${esc(r.location?.country || '')}</div>` +
		`<div class="loc clip">${esc(place(r.location))}</div>` +
		`<div class="when hide-s">${ago(r.achievedAt, now)}</div></div>`
	);
}

const LABELS =
	'<div class="row lbl"><div>#</div><div>Player</div><div class="sc">Score</div><div class="city">City</div>' +
	'<div class="st hide-s">State</div><div class="cc hide-s">Country</div><div class="loc">Location</div><div class="when hide-s">When</div></div>';

export function board(state) {
	const {game, board: b, page, filters: f, locations, error, stale, now = Date.now()} = state;
	if (!game)
		return '<div class="empty"><b>No games yet</b><span>Nothing has a leaderboard yet.</span></div>';
	const rows = b?.rows || [];
	const count = b?.count || 0;
	//	the board's top 3, on every page (page 1 carries them in its own rows)
	const top3 = page === 1 ? rows.slice(0, 3) : state.top3 || [];
	const strip = top3.length ? `<div class="top3">${chips(top3)}</div>` : '';
	const live = error
		? `<span class="live off">Scores unavailable, retrying</span>`
		: `<span class="live${stale ? ' off' : ''}">Live · updates every 30 s</span>`;
	const head =
		`<div class="head"><h2>${esc(game.title)}</h2><span>${count ? `${fmt(count)} players · ` : ''}all-time · ${esc(filterSummary(f))}</span>` +
		`${live}${game.players ? filters({filters: f, locations}) : ''}${strip}</div>`;

	if (!game.players) {
		return `${head}<div class="empty"><b>No scores yet</b><span>Be the first. Play ${esc(game.title)} on any Polycade AGS and save your score.</span></div>`;
	}
	if (!b) return `${head}<div class="empty"><b>Loading…</b></div>`;
	if (!rows.length) {
		return `${head}<div class="empty"><b>No scores from ${esc(filterSummary(f))} yet</b><span>Clear the filters, or be the first from there.</span></div>`;
	}

	const list = (page === 1 ? rows.slice(3) : rows).map((r) => row(r, now)).join('');
	const pod = top3.length ? `<div class="podium">${podium(top3, now)}</div>` : '';
	const pages = Math.max(1, Math.ceil(count / 50));
	return (
		`${head}${pod}<div class="table">${LABELS}${list}</div>` +
		`<div class="pager"><button type="button" data-page="${page - 1}"${page <= 1 ? ' disabled' : ''}>‹ Prev</button>` +
		`<span>${pageRange({page, shown: rows.length, count})}</span>` +
		`<button type="button" data-page="${page + 1}"${page >= pages ? ' disabled' : ''}>Next ›</button></div>`
	);
}
